import { DollarSign } from "lucide-react";
import { type JSX, memo } from "react";
import { useTranslation } from "react-i18next";
import IconToggleButton from "@/components/ui/icon-toggle-button";
import Tip from "@/components/ui/tip";
import { cn } from "@/lib/utils";

export interface BillableToggleProps {
	readonly "aria-label"?: string;
	readonly className?: string;
	readonly disabled?: boolean;
	readonly onPressedChange: (pressed: boolean) => void;
	readonly pressed: boolean;
	readonly size?: "default" | "sm" | "lg" | "icon-sm";
	readonly tip?: boolean;
	readonly type?: "button" | "submit" | "reset";
}

/** Shared billable on/off control: DollarSign, not status amber. */
function BillableToggle({
	pressed,
	onPressedChange,
	disabled = false,
	size = "icon-sm",
	className,
	tip = true,
	"aria-label": ariaLabel,
	type = "button",
}: BillableToggleProps): JSX.Element {
	const { t } = useTranslation();
	const label = ariaLabel ?? (pressed ? t("timer.billableYes") : t("timer.billableNo"));

	const control = (
		<IconToggleButton
			aria-label={label}
			className={className}
			disabled={disabled}
			onPressedChange={onPressedChange}
			pressed={pressed}
			size={size}
			type={type}
			variant="default">
			<DollarSign />
		</IconToggleButton>
	);

	if (!tip) {
		return control;
	}

	return <Tip content={label}>{control}</Tip>;
}

export interface BillableIconProps {
	readonly active?: boolean;
	readonly "aria-hidden"?: boolean | "true" | "false";
	readonly "aria-label"?: string;
	readonly className?: string;
}

/** Static billable marker. */
export function BillableIcon({
	active = true,
	className,
	"aria-label": ariaLabel,
	"aria-hidden": ariaHidden,
}: BillableIconProps): JSX.Element {
	return (
		<DollarSign
			aria-hidden={ariaHidden}
			aria-label={ariaLabel}
			className={cn("size-4 shrink-0", active ? "text-foreground" : "text-muted-foreground", className)}
		/>
	);
}

/** Table column title: "$" with accessible billable label. */
export function BillableTableColumnHeader(): JSX.Element {
	const { t } = useTranslation();
	return <span aria-label={t("settings.projects.billable")}>$</span>;
}

/** Table cell value: "Yes" / "No". */
export function BillableTableCell({ billable }: { readonly billable: boolean }): JSX.Element {
	const { t } = useTranslation();
	return (
		<span className="text-muted-foreground text-xs">
			{billable ? t("settings.projects.billableValueYes") : t("settings.projects.billableValueNo")}
		</span>
	);
}

export default memo(BillableToggle);
